// components/templates/sections/AboutSection.tsx
'use client';
import Image from 'next/image';
import Link from 'next/link';
import { useDesign } from '@/context/DesignContext';

interface AboutSectionProps {
  image: string;
  subtitle?: string;
  title: string;
  paragraphs: string[];
  buttonText?: string;
  buttonLink?: string;
  design?: any;
}

export default function AboutSection({
  image,
  subtitle = 'About Restaurant',
  title,
  paragraphs,
  buttonText = 'read more about us',
  buttonLink = '/about',
  design: propDesign,
}: AboutSectionProps) {
  const contextDesign = useDesign();
  const design = propDesign || contextDesign;

  // Extract values with fallbacks
  const headingFont = design?.typography?.headingFont || "'Sora', sans-serif";
  const bodyFont = design?.typography?.bodyFont || "'Plus Jakarta Sans', sans-serif";
  const sectionPaddingTop = design?.spacing?.sectionPaddingTop || '80px';
  const sectionPaddingBottom = design?.spacing?.sectionPaddingBottom || '80px';
  const btnStyle = design?.buttons?.outline || {
    class: 'tf-button style3',
    backgroundColor: 'transparent',
    borderColor: 'var(--primary)',
    textColor: 'var(--primary)',
    borderRadius: '30px',
    padding: '10px 25px',
  };
  const borderRadius = design?.effects?.borderRadius || '8px';

  // Inline styles
  const sectionStyle: React.CSSProperties = {
    padding: `${sectionPaddingTop} 0 ${sectionPaddingBottom} 0`,
  };
  const subtitleStyle: React.CSSProperties = {
    fontFamily: headingFont,
    fontSize: '1rem',
    color: design?.colors?.textLight || '#6C757D',
    marginBottom: '0.5rem',
  };
  const titleStyle: React.CSSProperties = {
    fontFamily: headingFont,
    fontSize: '2rem',
    fontWeight: design?.typography?.headingWeights?.h3 || 600,
    color: design?.colors?.text || '#1E1E1E',
    marginBottom: '1rem',
  };
  const textStyle: React.CSSProperties = {
    fontFamily: bodyFont,
    fontSize: design?.typography?.baseFontSize || '16px',
    lineHeight: 1.6,
    color: design?.colors?.text || '#1E1E1E',
    marginBottom: '1rem',
  };
  const buttonStyle: React.CSSProperties = {
    backgroundColor: btnStyle.backgroundColor,
    border: btnStyle.borderColor ? `1px solid ${btnStyle.borderColor}` : 'none',
    color: btnStyle.textColor,
    padding: btnStyle.padding,
    borderRadius: btnStyle.borderRadius,
    cursor: 'pointer',
    display: 'inline-block',
    textDecoration: 'none',
  };
  const imageStyle: React.CSSProperties = {
    borderRadius: borderRadius,
    overflow: 'hidden',
  };

  return (
    <section className="about" style={sectionStyle}>
      <div className="shape"></div>
      <div className="container" style={{ maxWidth: design?.spacing?.containerMaxWidth || '1200px', margin: '0 auto' }}>
        <div className="row">
          <div className="col-xl-6 col-md-12">
            <div className="about-img" data-aos="fade-right" data-aos-duration="1000" style={imageStyle}>
              <Image src={image} alt="" width={600} height={500} />
            </div>
          </div>
          <div className="col-xl-6 col-md-12">
            <div className="about-content">
              <div className="block-text">
                <p className="subtitle" data-aos="fade-up" data-aos-duration="1000" style={subtitleStyle}>{subtitle}</p>
                <h3 className="title" data-aos="fade-up" data-aos-duration="1000" style={titleStyle}>{title}</h3>
                {paragraphs.map((para, idx) => (
                  <p key={idx} className="text" data-aos="fade-up" data-aos-duration="1000" style={textStyle}>{para}</p>
                ))}
                <Link href={buttonLink} className={btnStyle.class} data-aos="fade-up" data-aos-duration="1000" style={buttonStyle}>
                  {buttonText}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}